// iter217 — Operating Expenses page (Phase 2)
// Admin/Owner only. CRUD for salaries, rent, electricity, packing, etc.
// Recurring rows auto-post via scheduler (00:05 IST daily).
// Overhead-tagged rows feed the equal-per-kg overhead allocation on batches.

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, X, Trash2, Pencil, Save, RefreshCw, Search, AlertCircle,
  Repeat, Calendar, Tag, Banknote, Lock, Calculator,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const CATEGORIES = [
  'salary', 'rent', 'electricity', 'transport', 'packing',
  'internet', 'fuel', 'utilities', 'maintenance', 'marketing',
  'professional_fees', 'interest', 'other',
];
const FREQUENCIES = ['one_time', 'monthly', 'quarterly', 'yearly'];
const OVERHEAD_BASIS = ['per_kg', 'per_batch'];
const PAID_VIA = ['bank', 'cash', 'upi', 'other'];

const CATEGORY_COLORS = {
  salary: 'bg-blue-900/40 text-blue-300 border-blue-700',
  rent: 'bg-purple-900/40 text-purple-300 border-purple-700',
  electricity: 'bg-yellow-900/40 text-yellow-300 border-yellow-700',
  transport: 'bg-orange-900/40 text-orange-300 border-orange-700',
  packing: 'bg-cyan-900/40 text-cyan-300 border-cyan-700',
  internet: 'bg-indigo-900/40 text-indigo-300 border-indigo-700',
  fuel: 'bg-amber-900/40 text-amber-300 border-amber-700',
  utilities: 'bg-teal-900/40 text-teal-300 border-teal-700',
  maintenance: 'bg-rose-900/40 text-rose-300 border-rose-700',
  marketing: 'bg-pink-900/40 text-pink-300 border-pink-700',
  professional_fees: 'bg-fuchsia-900/40 text-fuchsia-300 border-fuchsia-700',
  interest: 'bg-red-900/40 text-red-300 border-red-700',
  other: 'bg-zinc-800 text-zinc-400 border-zinc-700',
};

const EMPTY_FORM = {
  category: 'salary',
  label: '',
  amount: '',
  date: new Date().toISOString().slice(0, 10),
  frequency: 'monthly',
  is_overhead: false,
  overhead_basis: 'per_kg',
  paid_via: 'bank',
  notes: '',
};

export default function Expenses() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7));
  const [categoryFilter, setCategoryFilter] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // expense being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [postRecurring, setPostRecurring] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listRes, sumRes, rateRes] = await Promise.all([
        axios.get(`${API}/expenses`, {
          params: { month: monthFilter, category: categoryFilter },
          withCredentials: true,
        }),
        axios.get(`${API}/expenses/summary`, {
          params: { month: monthFilter },
          withCredentials: true,
        }),
        axios.get(`${API}/expenses/overhead-rate`, {
          params: { days: 30 },
          withCredentials: true,
        }).catch(() => ({ data: null })),
      ]);
      setExpenses(listRes.data || []);
      setSummary(sumRes.data || null);
      setRate(rateRes.data || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load expenses');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [monthFilter, categoryFilter]);

  const filtered = useMemo(() => {
    const q = (search || '').toLowerCase().trim();
    if (!q) return expenses;
    return expenses.filter(e =>
      (e.label || '').toLowerCase().includes(q)
      || (e.category || '').toLowerCase().includes(q)
      || (e.notes || '').toLowerCase().includes(q)
      || (e.paid_via || '').toLowerCase().includes(q)
    );
  }, [expenses, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      category: e.category,
      label: e.label || '',
      amount: e.amount,
      date: e.date,
      frequency: e.frequency,
      is_overhead: !!e.is_overhead,
      overhead_basis: e.overhead_basis || 'per_kg',
      paid_via: e.paid_via || 'bank',
      notes: e.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (!payload.is_overhead) payload.overhead_basis = null;
      if (editing) {
        await axios.patch(`${API}/expenses/${editing.id}`, payload, { withCredentials: true });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/expenses`, payload, { withCredentials: true });
        toast.success(form.frequency === 'one_time' ? 'Expense posted' : 'Recurring expense set up');
      }
      setShowModal(false);
      await fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (e) => {
    if (!window.confirm(`Delete "${e.label || e.category}" — ${fmt(e.amount)}?`)) return;
    try {
      await axios.delete(`${API}/expenses/${e.id}`, { withCredentials: true });
      toast.success('Expense deleted');
      await fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const handlePostRecurring = async () => {
    setPostRecurring(true);
    try {
      const r = await axios.post(`${API}/expenses/post-recurring`, {}, { withCredentials: true });
      toast.success(`Posted ${r.data.posted} due recurring entries`);
      await fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setPostRecurring(false); }
  };

  if (!isAdmin) return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 flex items-center gap-3" data-testid="expenses-no-access">
      <Lock size={20} className="text-amber-400" />
      <div>
        <div className="text-zinc-50 font-semibold">Admin-only area</div>
        <div className="text-zinc-500 text-sm">Operating Expenses + Overhead allocation are admin/owner only.</div>
      </div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="expenses-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50">Operating Expenses</h1>
          <p className="text-zinc-500 text-sm mt-0.5">
            Salaries · Rent · Utilities · Direct opex. Auto-posts monthly recurring rows. Powers per-kg overhead allocation on batches.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handlePostRecurring} disabled={postRecurring} data-testid="post-recurring-btn"
            title="Force-post any due monthly/quarterly/yearly expenses now (cron also runs this at 00:05 IST daily)"
            className="flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs px-3 py-2 rounded-xl border border-zinc-700 disabled:opacity-50">
            <Repeat size={13} /> {postRecurring ? 'Posting…' : 'Post Due Recurring'}
          </button>
          <button onClick={openCreate} data-testid="add-expense-btn"
            className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs px-3 py-2 rounded-xl">
            <Plus size={14} /> New Expense
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4" data-testid="kpi-total-month">
          <div className="text-zinc-400 text-xs mb-1">Total ({monthFilter})</div>
          <div className="font-mono font-bold text-2xl text-amber-300">{fmt(summary?.total)}</div>
          <div className="text-zinc-500 text-[10px] mt-0.5">{summary?.row_count || 0} entries</div>
        </div>
        <div className="bg-zinc-900 border border-emerald-800/40 rounded-xl p-4" data-testid="kpi-overhead">
          <div className="text-emerald-400 text-xs mb-1 flex items-center gap-1"><Calculator size={11} /> Overheads</div>
          <div className="font-mono font-bold text-2xl text-emerald-300">{fmt(summary?.overhead_total)}</div>
          <div className="text-zinc-500 text-[10px] mt-0.5">Allocated to batches</div>
        </div>
        <div className="bg-zinc-900 border border-blue-800/40 rounded-xl p-4" data-testid="kpi-recurring">
          <div className="text-blue-400 text-xs mb-1 flex items-center gap-1"><Repeat size={11} /> Recurring (mo)</div>
          <div className="font-mono font-bold text-2xl text-blue-300">{fmt(summary?.by_frequency?.monthly)}</div>
          <div className="text-zinc-500 text-[10px] mt-0.5">Auto-posts on the 1st</div>
        </div>
        <div className="bg-zinc-900 border border-amber-800/40 rounded-xl p-4" data-testid="kpi-rate-per-kg">
          <div className="text-amber-400 text-xs mb-1">Overhead Rate /kg (30d)</div>
          <div className="font-mono font-bold text-2xl text-amber-300">{fmt(rate?.rate_per_kg)}</div>
          <div className="text-zinc-500 text-[10px] mt-0.5">{rate?.output_kg ? `${rate.output_kg} kg produced` : 'No production data'}</div>
        </div>
      </div>

      {/* By-category breakdown */}
      {summary?.by_category && Object.keys(summary.by_category).length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
          <div className="text-zinc-400 text-xs font-medium mb-3 flex items-center gap-2">
            <Tag size={12} /> Category Breakdown — {monthFilter}
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-breakdown">
            {Object.entries(summary.by_category).map(([cat, amt]) => (
              <button key={cat} onClick={() => setCategoryFilter(cat === categoryFilter ? '' : cat)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-mono transition-all ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.other} ${categoryFilter === cat ? 'ring-2 ring-amber-400' : ''}`}>
                <span className="capitalize">{cat.replace(/_/g, ' ')}</span>
                <span className="font-bold">{fmt(amt)}</span>
              </button>
            ))}
            {categoryFilter && (
              <button onClick={() => setCategoryFilter('')} className="text-[11px] text-zinc-400 hover:text-amber-300 px-2 py-1">Clear filter</button>
            )}
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-3 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-zinc-950 border border-zinc-700 rounded-xl px-3 py-1.5 flex-1 min-w-[200px]">
          <Search size={13} className="text-zinc-500" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, category, notes…"
            data-testid="expense-search"
            className="bg-transparent flex-1 text-zinc-50 text-sm focus:outline-none" />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-zinc-500 text-xs">Month:</label>
          <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)}
            data-testid="month-filter"
            className="bg-zinc-950 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs" />
        </div>
        <button onClick={fetchData} className="text-zinc-400 hover:text-amber-300 p-2" title="Refresh">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-zinc-500 text-sm animate-pulse text-center py-12">Loading expenses…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center" data-testid="expenses-empty">
          <AlertCircle size={20} className="text-zinc-500 mx-auto mb-2" />
          <div className="text-zinc-400 text-sm">No expenses {search ? 'matching your search' : 'this month'}.</div>
          <div className="text-zinc-500 text-xs mt-1">Click &quot;New Expense&quot; to record one.</div>
        </div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead className="bg-zinc-950/50">
                <tr className="border-b border-zinc-800">
                  {['Date', 'Category', 'Label / Notes', 'Frequency', 'Paid Via', 'Amount', ''].map(h => (
                    <th key={h} className="text-left text-zinc-500 text-xs font-medium px-3 py-2.5">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(e => (
                  <tr key={e.id} className="border-b border-zinc-800/40 hover:bg-zinc-800/30" data-testid={`expense-row-${e.id}`}>
                    <td className="px-3 py-2.5 font-mono text-zinc-300 text-xs">{e.date}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize font-mono ${CATEGORY_COLORS[e.category] || CATEGORY_COLORS.other}`}>
                        {e.category.replace(/_/g, ' ')}
                      </span>
                      {e.is_overhead && (
                        <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-300 border border-emerald-700">OVH</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-zinc-200 text-xs">
                      <div className="font-medium">{e.label || <span className="text-zinc-500">—</span>}</div>
                      {e.notes && <div className="text-[10px] text-zinc-500 mt-0.5 max-w-md truncate">{e.notes}</div>}
                      {e.parent_recurring_id && <div className="text-[9px] text-zinc-600 mt-0.5">auto-posted</div>}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border capitalize ${e.is_recurring ? 'bg-blue-900/40 text-blue-300 border-blue-700' : 'bg-zinc-800 text-zinc-400 border-zinc-700'}`}>
                        {e.frequency.replace(/_/g, ' ')}
                      </span>
                      {e.is_recurring && e.next_post_date && (
                        <div className="text-[9px] text-zinc-500 mt-0.5 font-mono flex items-center gap-1">
                          <Calendar size={9} /> next: {e.next_post_date}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-xs text-zinc-400 flex items-center gap-1">
                        <Banknote size={11} className="text-zinc-500" />
                        <span className="capitalize">{e.paid_via}</span>
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-mono text-sm font-semibold text-amber-300">{fmt(e.amount)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(e)} data-testid={`edit-expense-${e.id}`}
                          className="p-1.5 rounded bg-zinc-800 hover:bg-amber-900/40 text-zinc-400 hover:text-amber-300 border border-zinc-700">
                          <Pencil size={11} />
                        </button>
                        <button onClick={() => handleDelete(e)} data-testid={`delete-expense-${e.id}`}
                          className="p-1.5 rounded bg-red-900/30 hover:bg-red-900/50 text-red-400 border border-red-800/30">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-md max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800 sticky top-0 bg-zinc-900">
              <h3 className="font-heading font-semibold text-zinc-50">
                {editing ? 'Edit Expense' : 'New Operating Expense'}
              </h3>
              <button onClick={() => setShowModal(false)}><X size={18} className="text-zinc-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-3" data-testid="expense-form">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Category *</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    data-testid="form-category"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:border-amber-500 focus:outline-none">
                    {CATEGORIES.map(c => <option key={c} value={c} className="bg-zinc-900 capitalize">{c.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Amount (₹) *</label>
                  <input type="number" step="0.01" required min="0.01" value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    data-testid="form-amount"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:border-amber-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Label / Description</label>
                <input value={form.label} onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                  data-testid="form-label" placeholder="e.g., December salaries, Q1 office rent, …"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm focus:border-amber-500 focus:outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Date *</label>
                  <input type="date" required value={form.date}
                    onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                    data-testid="form-date"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm" />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">Frequency *</label>
                  <select value={form.frequency}
                    onChange={e => setForm(f => ({ ...f, frequency: e.target.value }))}
                    data-testid="form-frequency"
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-sm">
                    {FREQUENCIES.map(f => <option key={f} value={f} className="bg-zinc-900 capitalize">{f.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Paid Via</label>
                <div className="flex gap-2">
                  {PAID_VIA.map(p => (
                    <button key={p} type="button" onClick={() => setForm(f => ({ ...f, paid_via: p }))}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${form.paid_via === p ? 'bg-amber-500 text-black font-semibold' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-zinc-950/60 border border-zinc-800 rounded-xl p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_overhead}
                    onChange={e => setForm(f => ({ ...f, is_overhead: e.target.checked }))}
                    data-testid="form-overhead"
                    className="accent-emerald-500" />
                  <span className="text-zinc-200 text-sm font-medium">Mark as Overhead</span>
                  <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-900/40 text-emerald-300 border border-emerald-700">Phase 2</span>
                </label>
                <div className="text-zinc-500 text-[11px] mt-1 ml-6">
                  Overheads get allocated to production batches via equal-per-kg rule (₹/kg of output).
                </div>
                {form.is_overhead && (
                  <div className="mt-2 ml-6">
                    <label className="text-zinc-400 text-xs mb-1 block">Allocation Basis</label>
                    <select value={form.overhead_basis}
                      onChange={e => setForm(f => ({ ...f, overhead_basis: e.target.value }))}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-2 py-1.5 text-zinc-50 text-xs">
                      {OVERHEAD_BASIS.map(b => <option key={b} value={b} className="bg-zinc-900">{b.replace('_', ' / ')}</option>)}
                    </select>
                  </div>
                )}
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  data-testid="form-notes" rows={2}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-3 py-2 text-zinc-50 text-xs focus:border-amber-500 focus:outline-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 text-zinc-400 text-sm">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="submit-expense-btn"
                  className="flex-1 py-2.5 rounded-xl bg-amber-500 text-black font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-1.5">
                  <Save size={13} /> {submitting ? 'Saving…' : (editing ? 'Update' : 'Add Expense')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
